import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, Palette, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUploadDraft } from "@/contexts/UploadDraftContext";
import usePageMeta from "@/hooks/usePageMeta";

/**
 * /get-started — the single question route.
 *
 * Locked spec (Stage 2 · 2026-02):
 *   "What do you want to do today?"
 *     🏡 I'm visualising my home — Experiment with colours on my own property.
 *     🎨 I'm creating renders for clients — Build professional visualisations and proposals.
 *
 * Reached only when a homepage upload happened AND the user's role is unknown
 * (signed-out). If a signed-in user lands here, we route them straight to the
 * correct experience — no question asked (role is known).
 *
 * If no upload draft exists, we do not ask the question; we send the user back
 * to the homepage to make a real choice from the two upload buttons.
 */
export default function GetStarted() {
  usePageMeta({
    title: "Get started — ColourMatch",
    description: "Choose whether you're visualising your own home or creating renders for clients.",
    path: "/get-started",
  });

  const nav = useNavigate();
  const { user } = useAuth();
  const { hasDraft, ready, clearDraft } = useUploadDraft();
  const isSignedIn = !!user && user !== false;

  // If a signed-in user reaches this route directly, respect the "role is known"
  // rule and skip the question.
  useEffect(() => {
    if (!ready) return;
    if (!isSignedIn) return;
    if (user?.is_painter) {
      nav("/pro/projects", { replace: true });
    } else {
      nav("/visualiser/new", { replace: true });
    }
  }, [ready, isSignedIn, user, nav]);

  // No draft → send them back to make an intentional choice from the homepage.
  if (ready && !hasDraft && !isSignedIn) {
    return (
      <div className="min-h-[70vh] bg-brand-bg flex items-center justify-center px-5">
        <div className="max-w-md text-center" data-testid="get-started-no-draft">
          <h1 className="text-2xl font-extrabold text-brand-ink mb-3">Start with a photo</h1>
          <p className="text-brand-muted mb-6">
            Take a picture or upload a photo of the home first, and we&rsquo;ll take it from there.
          </p>
          <Link
            to="/"
            data-testid="get-started-back-home"
            className="inline-flex items-center gap-2 bg-brand-navy text-white h-11 px-5 rounded-2xl text-sm font-semibold hover:bg-brand-navy/90 transition-all"
          >
            Back to homepage <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const chooseHomeowner = () => {
    // Preserve the draft; route to sign-up which lands at /visualiser/new where the draft is consumed.
    nav("/register?from=/visualiser/new");
  };

  const choosePainter = async () => {
    // Q-B3(b) — do not imply the image has been saved through a multi-day approval flow.
    // Discard the draft cleanly and route to Pro registration.
    await clearDraft();
    nav("/pro/register");
  };

  return (
    <div className="min-h-[70vh] bg-brand-bg py-14 lg:py-20 px-5 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-center" data-testid="get-started-header">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal mb-3">
            Your photo is ready
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-brand-ink leading-tight">
            What do you want to do today?
          </h1>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Homeowner card */}
          <button
            type="button"
            onClick={chooseHomeowner}
            data-testid="get-started-homeowner"
            className="text-left bg-white border border-brand-border rounded-3xl p-7 lg:p-8 hover:shadow-lg hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2"
          >
            <span className="w-12 h-12 rounded-2xl bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-5">
              <Home size={22} aria-hidden="true" />
            </span>
            <h2 className="text-xl font-bold text-brand-ink mb-1.5">I&rsquo;m visualising my home</h2>
            <p className="text-sm text-brand-muted leading-relaxed">
              Experiment with colours on my own property.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-navy">
              Continue <ArrowRight size={14} />
            </span>
          </button>

          {/* Painter card */}
          <button
            type="button"
            onClick={choosePainter}
            data-testid="get-started-painter"
            className="text-left bg-white border border-brand-border rounded-3xl p-7 lg:p-8 hover:shadow-lg hover:-translate-y-0.5 transition-all focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2"
          >
            <span className="w-12 h-12 rounded-2xl bg-brand-navy/10 text-brand-navy flex items-center justify-center mb-5">
              <Palette size={22} aria-hidden="true" />
            </span>
            <h2 className="text-xl font-bold text-brand-ink mb-1.5">I&rsquo;m creating renders for clients</h2>
            <p className="text-sm text-brand-muted leading-relaxed">
              Build professional visualisations and proposals.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-navy">
              Continue <ArrowRight size={14} />
            </span>
          </button>
        </div>

        <p className="mt-8 text-xs text-brand-muted text-center" data-testid="get-started-fineprint">
          Painter accounts require approval before you can create professional client projects. If you choose the painter path, your staged photo will not be carried through the approval process.
        </p>
      </div>
    </div>
  );
}
